import { FC } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { SelectedCompanyCurrency } from '../../../../components/selected-company-currency/index';
import { useFormat } from '../../../../hooks/index';
import { StringKey } from '../../../../lang/index';
import { EventTypeBackend } from '../../../../types/events.types';
import { SharePlanType } from '../../../../types/pool-plans.types';
import { toNumber } from '../../../../utils/to-number.util';
import { CardProps } from './transaction-card-variants.types';
import { TransactionOverviewItem } from './transaction-overview-item';
import { TransactionTitleItem } from './transaction-title-item';

const planLabelKey: Partial<Record<SharePlanType, StringKey>> = {
  [SharePlanType.STOCK_OPTIONS]: 'EXERCISE_STOCK_OPTIONS',
  [SharePlanType.WARRANTS]: 'EXERCISE_EXPIRE_WARRANTS',
  [SharePlanType.GROWTH_HURDLE]: 'HURDLE_APPROVAL',
  [SharePlanType.SAR]: 'HURDLE_APPROVAL',
  [SharePlanType.BSPCE]: 'EXERCISE_BSPCE',
  [SharePlanType.COMMON_STOCK]: 'EXERCISE',
  [SharePlanType.PHANTOM]: 'EXERCISE',
};

export const Exercised: FC<CardProps> = ({ event }) => {
  const { format } = useFormat();

  if (event.type !== EventTypeBackend.EXERCISE) return <></>;

  const {
    exercise: { items },
    createdAt,
  } = event;

  const eventIdShort = event.eventId.split('-')[0];
  const planType = items[0]?.vestingTask?.grantItem?.plan?.type;

  if (!planType || items.length === 0) return <></>;

  const mappedPlanLabel = planLabelKey[planType];
  const transactionTypeLabel: StringKey = mappedPlanLabel ?? 'EXERCISE';

  if (planType === SharePlanType.STOCK_OPTIONS || planType === SharePlanType.BSPCE)
    return (
      <>
        {items.map(({ id, vestingTask, exercised }) => (
          <div className="flex flex-col divide-y divide-gray-100 rounded-md shadow-sm" key={id}>
            <TransactionTitleItem
              createdAt={format(createdAt, 'dd/MM/yyyy')}
              name={<AppFormattedMessage id="EXERCISED" />}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="EVENT_ID" />}
              value={<span className="uppercase">{eventIdShort}</span>}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="TRANSACTION_TYPE" />}
              value={<AppFormattedMessage id={transactionTypeLabel} />}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="STAKEHOLDER" />}
              value={vestingTask.grantItem.stakeholder.fullName}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="GRANT_ID" />}
              value={<span className="uppercase">{vestingTask.grantItem.id.split('-')[0]}</span>}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="EXERCISED_OPTIONS" />}
              value={toNumber(exercised).toLocaleString('en-US')}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="SHARE_CLASS" />}
              value={vestingTask.grantItem.plan.pool.shareClass.name}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="STRIKE_PRICE" />}
              value={
                vestingTask.grantItem.plan.strikePrice != null ? (
                  <>
                    <SelectedCompanyCurrency />
                    {toNumber(vestingTask.grantItem.plan.strikePrice).toLocaleString('en-US')}
                  </>
                ) : (
                  '-'
                )
              }
            />
          </div>
        ))}
      </>
    );

  if (planType === SharePlanType.WARRANTS)
    return (
      <>
        {items.map(({ id, vestingTask, exercised }) => {
          const exercisedWarrants =
            exercised / toNumber(vestingTask.grantItem.plan.conversionRatio, 1);

          return (
            <div className="flex flex-col divide-y divide-gray-100 rounded-md shadow-sm" key={id}>
              <TransactionTitleItem
                createdAt={format(createdAt, 'dd/MM/yyyy')}
                name={<AppFormattedMessage id="EXERCISED" />}
              />
              <TransactionOverviewItem
                title={<AppFormattedMessage id="EVENT_ID" />}
                value={<span className="uppercase">{eventIdShort}</span>}
              />
              <TransactionOverviewItem
                title={<AppFormattedMessage id="TRANSACTION_TYPE" />}
                value={<AppFormattedMessage id={transactionTypeLabel} />}
              />
              <TransactionOverviewItem
                title={<AppFormattedMessage id="STAKEHOLDER" />}
                value={vestingTask.grantItem.stakeholder.fullName}
              />
              <TransactionOverviewItem
                title={<AppFormattedMessage id="GRANT_ID" />}
                value={<span className="uppercase">{vestingTask.grantItem.id.split('-')[0]}</span>}
              />
              <TransactionOverviewItem
                title={<AppFormattedMessage id="EXERCISED_WARRANTS" />}
                value={exercisedWarrants.toLocaleString('en-US')}
              />
              <TransactionOverviewItem
                title={<AppFormattedMessage id="SHARE_CLASS" />}
                value={vestingTask.grantItem.plan.pool.shareClass.name}
              />
            </div>
          );
        })}
      </>
    );

  if (planType === SharePlanType.SAR)
    return (
      <>
        {items.map(({ id, vestingTask, exercised, payout }) => (
          <div className="flex flex-col divide-y divide-gray-100 rounded-md shadow-sm" key={id}>
            <TransactionTitleItem
              createdAt={format(createdAt, 'dd/MM/yyyy')}
              name={<AppFormattedMessage id="EXERCISED" />}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="EVENT_ID" />}
              value={<span className="uppercase">{eventIdShort}</span>}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="TRANSACTION_TYPE" />}
              value={<AppFormattedMessage id={transactionTypeLabel} />}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="STAKEHOLDER" />}
              value={vestingTask.grantItem.stakeholder.fullName}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="GRANT_ID" />}
              value={<span className="uppercase">{vestingTask.grantItem.id.split('-')[0]}</span>}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="SAR_EXERCISED" />}
              value={exercised.toLocaleString('en-US')}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="SHARE_CLASS" />}
              value={vestingTask.grantItem.plan.pool.shareClass.name}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="SAR_PAYOUT" />}
              value={
                <>
                  <SelectedCompanyCurrency />
                  {payout != null ? payout.toLocaleString('en-US') : '—'}
                </>
              }
            />
          </div>
        ))}
      </>
    );

  return <></>;
};
